/**
 * Vault crypto — AES-GCM-256 with PBKDF2(SHA-256) key derivation.
 * Runs entirely in the browser. The server never sees the passphrase or plaintext.
 *
 * If No. 6 forgets the passphrase, the entry is unrecoverable. That is the point.
 */

const ITERATIONS = 200000;
const enc = new TextEncoder();
const dec = new TextDecoder();

function b64encode(buf) {
    const bytes = new Uint8Array(buf);
    let s = "";
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s);
}
function b64decode(s) {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out.buffer;
}

async function deriveKey(passphrase, salt) {
    const baseKey = await crypto.subtle.importKey(
        "raw",
        enc.encode(passphrase),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );
    return crypto.subtle.deriveKey(
        { name: "PBKDF2", salt, iterations: ITERATIONS, hash: "SHA-256" },
        baseKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
    );
}

export async function sealPlaintext(passphrase, plaintext) {
    if (!passphrase || !plaintext) throw new Error("Passphrase and content required");
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(passphrase, salt);
    const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        enc.encode(plaintext)
    );
    return {
        ciphertext: b64encode(ct),
        iv: b64encode(iv),
        salt: b64encode(salt),
        iterations: ITERATIONS,
        algo: "AES-GCM-256/PBKDF2-SHA256",
    };
}

export async function openCiphertext(passphrase, blob) {
    const salt = b64decode(blob.salt);
    const iv = b64decode(blob.iv);
    const ct = b64decode(blob.ciphertext);
    const key = await deriveKey(passphrase, new Uint8Array(salt));
    try {
        const pt = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: new Uint8Array(iv) },
            key,
            ct
        );
        return dec.decode(pt);
    } catch {
        throw new Error("Wrong passphrase, or the file has been tampered with.");
    }
}
